// Shared fetch + cache layer for /api/builder/* endpoints. Module-level
// promise cache so multiple Editor instances on the same page reuse the same
// in-flight request. Cache survives until the page is reloaded (no
// invalidation needed — the species catalog and item list are effectively
// static for the regulation's lifetime).

export type BuilderSpecies = {
  id: string;
  name: string;
  types: string[];
  base_stats: { hp: number; atk: number; def: number; spa: number; spd: number; spe: number };
  abilities: string[];
  // Pikalytics usage % for the active regulation (base + mega forms summed
  // for base rows; raw pikalytics usage for mega rows). 0 when no snapshot.
  usage_pct: number;
  // True for mega-form synthetic rows (e.g. "aerodactyl-mega"). Only
  // present when the catalog was fetched with include_megas=true. The team
  // builder hides these (you pick base + equip stone); the rail picker
  // shows them (so users can pin a mega as a defender).
  is_mega?: boolean;
};

export type BuilderTopSet = {
  form: string;
  player: string;
  record: string;
  item: string | null;
  ability: string | null;
  moves: string[];
  modal_nature: string | null;
  modal_ev_spread: string | null;
};

export type BuilderForm = {
  species_name: string;
  is_mega: boolean;
  modal_nature: string | null;
  modal_ev_spread: string | null;
  modal_pct: number | null;
  top_item: string | null;
  top_ability: string | null;
};

export type BuilderSpeciesOptions = {
  species_id: string;
  species_name: string;
  legal_abilities: string[];
  mega_abilities: string[];
  seen_moves: string[];
  top_sets: BuilderTopSet[];
  forms: BuilderForm[];
};

// Separate caches per include-megas mode so the team-builder picker (base
// species only) and the rail picker (base + mega forms) don't share a
// payload. Keyed by `${regulationId}:${includeMegas ? 'megas' : 'base'}`.
const speciesCatalogCache = new Map<string, Promise<BuilderSpecies[]>>();
let itemListCache: Promise<string[]> | null = null;
const speciesOptionsCache = new Map<string, Promise<BuilderSpeciesOptions>>();

export function getSpeciesCatalog(
  regulationId = 'M-A',
  includeMegas = false,
): Promise<BuilderSpecies[]> {
  const key = `${regulationId}:${includeMegas ? 'megas' : 'base'}`;
  let cached = speciesCatalogCache.get(key);
  if (!cached) {
    const q = new URLSearchParams({ regulation_id: regulationId });
    if (includeMegas) q.set('include_megas', '1');
    cached = fetch(`/api/builder/species?${q.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((body) => (body as { species: BuilderSpecies[] }).species)
      .catch((err) => {
        speciesCatalogCache.delete(key);
        throw err;
      });
    speciesCatalogCache.set(key, cached);
  }
  return cached;
}

export function getItemList(regulationId = 'M-A'): Promise<string[]> {
  if (!itemListCache) {
    itemListCache = fetch(`/api/builder/items?regulation_id=${regulationId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((body) => (body as { items: string[] }).items)
      .catch((err) => {
        itemListCache = null;
        throw err;
      });
  }
  return itemListCache;
}

export function getSpeciesOptions(
  speciesId: string,
  regulationId = 'M-A',
): Promise<BuilderSpeciesOptions> {
  const key = `${regulationId}:${speciesId}`;
  let cached = speciesOptionsCache.get(key);
  if (!cached) {
    cached = fetch(`/api/builder/species/${speciesId}/options?regulation_id=${regulationId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((body) => body as BuilderSpeciesOptions)
      .catch((err) => {
        speciesOptionsCache.delete(key);
        throw err;
      });
    speciesOptionsCache.set(key, cached);
  }
  return cached;
}
